import { useSyncExternalStore } from 'react';

/**
 * What was picked in the header: one customer, or one environment of it. The overview pages (Dashboard, Omgevingen,
 * Issues, Exact) show only that until "Alle klanten" is picked; the pages of one environment follow their URL.
 * Remembered in this browser, so it survives a reload.
 */
export interface Focus {
    customer: string;
    environment?: string;
}

const KEY = 'v2.focus';

const read = (): Focus | null => {
    try {
        const raw = window.localStorage.getItem(KEY);
        const parsed = raw ? (JSON.parse(raw) as Focus) : null;
        return parsed?.customer ? parsed : null;
    } catch {
        return null;
    }
};

let current: Focus | null = read();
const listeners = new Set<() => void>();

export const setFocus = (focus: Focus | null) => {
    current = focus?.customer ? { customer: focus.customer, environment: focus.environment || undefined } : null;
    try {
        if (current) window.localStorage.setItem(KEY, JSON.stringify(current));
        else window.localStorage.removeItem(KEY);
    } catch {
        // Storage that is off only costs remembering it after a reload.
    }
    listeners.forEach((l) => l());
};

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const useFocus = () => useSyncExternalStore(subscribe, () => current);

/** Whether an environment falls inside the focus; everything does without one. */
export const inFocus = (focus: Focus | null, e: { customer: string; environment: string }) =>
    !focus ||
    (e.customer.toLowerCase() === focus.customer.toLowerCase() && (!focus.environment || e.environment.toLowerCase() === focus.environment.toLowerCase()));

/** "orbisk", "orbisk prod". */
export const focusLabel = (focus: Focus) => (focus.environment ? `${focus.customer} ${focus.environment}` : focus.customer);
